const ALLOWED_TAGS = new Set(['p', 'br', 'strong', 'em', 'b', 'i', 'u', 'ul', 'ol', 'li', 'a', 'span']);
const REMOVED_WITH_CONTENT_TAGS = new Set(['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta']);
const ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

const isSafeHref = (href: string): boolean => {
  const trimmedHref = href.trim();

  if (!trimmedHref) {
    return false;
  }

  if (trimmedHref.startsWith('/') || trimmedHref.startsWith('#')) {
    return true;
  }

  try {
    const parsed = new URL(trimmedHref, window.location.origin);
    return ALLOWED_PROTOCOLS.includes(parsed.protocol);
  } catch {
    return false;
  }
};

const unwrapElement = (element: Element): void => {
  const parent = element.parentNode;

  if (!parent) {
    return;
  }

  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }

  parent.removeChild(element);
};

const sanitizeAnchorAttributes = (element: Element): void => {
  const href = element.getAttribute('href') || '';

  if (!isSafeHref(href)) {
    element.removeAttribute('href');
  }

  const target = element.getAttribute('target');
  if (target && target !== '_blank') {
    element.removeAttribute('target');
  }

  if (element.getAttribute('target') === '_blank') {
    element.setAttribute('rel', 'noopener noreferrer');
  } else {
    element.removeAttribute('rel');
  }
};

export const sanitizeHtml = (input: string): string => {
  if (!input) {
    return '';
  }

  const parser = new DOMParser();
  const document = parser.parseFromString(input, 'text/html');

  Array.from(document.body.querySelectorAll('*')).forEach((element) => {
    const tagName = element.tagName.toLowerCase();

    if (REMOVED_WITH_CONTENT_TAGS.has(tagName)) {
      element.remove();
      return;
    }

    if (!ALLOWED_TAGS.has(tagName)) {
      unwrapElement(element);
      return;
    }

    if (tagName === 'a') {
      Array.from(element.attributes).forEach((attribute) => {
        const attributeName = attribute.name.toLowerCase();
        if (!['href', 'target', 'rel'].includes(attributeName)) {
          element.removeAttribute(attribute.name);
        }
      });
      sanitizeAnchorAttributes(element);
      return;
    }

    Array.from(element.attributes).forEach((attribute) => {
      element.removeAttribute(attribute.name);
    });
  });

  return document.body.innerHTML;
};
